import dayjs from 'dayjs';
import { stripHtmlForDisplay } from '../../../../../../ui/html/stripHtmlForDisplay.tsx';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import {
  plainTextToRichText,
  stringWithTagsToRichText,
} from './normalizeColumnValue.ts';

function convertToDate(value: any): string {
  let date = dayjs(value, 'YYYY-MM-DD', true);
  if (!date.isValid()) date = dayjs(value, ['DD/MM/YYYY', 'DD/MM/YY'], true);
  if (!date.isValid()) date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
}

function convertToBoolean(value: any): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    return (
      lower === 'true' || lower === 'sì' || lower === 'vero' || lower === '1'
    );
  }
  return false;
}

function convertToString(value: any, sourceType: Datatype): string {
  if (sourceType === Datatype.RichText) {
    return stripHtmlForDisplay(String(value));
  }
  if (sourceType === Datatype.Date) {
    const date = dayjs(value, 'YYYY-MM-DD', true);
    return date.isValid() ? date.format('DD/MM/YYYY') : String(value);
  }
  if (sourceType === Datatype.Boolean) {
    return value === true || value === 'true' ? 'Sì' : 'No';
  }
  return String(value);
}

function convertToNumber(value: any): number | string {
  const num = Number(value);
  return Number.isNaN(num) ? '' : num;
}

/**
 * Converte un valore di tipo qualsiasi nel tipo destinazione richiesto.
 * Non gestisce il RichText (delegato ai rami dedicati).
 */
function convertToPlainType(
  value: any,
  sourceType: Datatype,
  targetType: Datatype,
) {
  if (targetType === Datatype.String) return convertToString(value, sourceType);
  if (targetType === Datatype.Number) return convertToNumber(value);
  if (targetType === Datatype.Boolean) return convertToBoolean(value);
  if (targetType === Datatype.Date) return convertToDate(value);
  return '';
}

/**
 * Converte un valore RichText (HTML) verso un tipo non formattato, partendo dal
 * testo grezzo estratto dall'HTML.
 */
function convertFromRichText(value: any, targetType: Datatype) {
  const plainValue = stripHtmlForDisplay(String(value));
  return convertToPlainType(plainValue, Datatype.String, targetType);
}

/**
 * Promuove un valore a RichText. La sorgente String può contenere tag base
 * importati da CSV (`<b>`, `<i>`, `<br>`): si preservano e si rendono espliciti
 * gli a-capo come `<br>`. Gli altri tipi non sono HTML e vanno promossi a
 * paragrafo.
 */
function convertToRichText(value: any, sourceType: Datatype): string {
  if (sourceType === Datatype.String) {
    return stringWithTagsToRichText(String(value));
  }
  return plainTextToRichText(convertToString(value, sourceType));
}

/**
 * Converte un valore dal tipo sorgente al tipo destinazione.
 */
export function convertColumnValue(
  value: any,
  sourceType: Datatype,
  targetType: Datatype,
): any {
  if (sourceType === targetType || value == null || value === '') {
    return value;
  }

  if (targetType === Datatype.RichText) {
    return convertToRichText(value, sourceType);
  }

  if (sourceType === Datatype.RichText) {
    return convertFromRichText(value, targetType);
  }

  return convertToPlainType(value, sourceType, targetType);
}
